import { ExternalLink } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { ActionMenu } from '@/components/fleet/action-menu';
import { ServerActionPanels } from '@/components/fleet/server-action-panels';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFleetAction } from '@/hooks/use-fleet-action';
import { useFleetServer } from '@/hooks/use-fleet-server';
import { useLifecycleAction } from '@/hooks/use-lifecycle-action';
import type { ServerDetail } from '@/types/fleet';

interface ServerRowActionsProps {
  serverName: string;
  /** Whether this server's menu is the one open. */
  open: boolean;
  /** Closes this server's menu. */
  onClose: (name: string) => void;
  /** Menu closed and no run in flight or tracked: the host may unmount. */
  onIdle: (name: string) => void;
}

function ServerPageLink({ name }: { name: string }): JSX.Element {
  return (
    <Link
      to={`/fleet/${encodeURIComponent(name)}`}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      Open server page (task history, editors)
    </Link>
  );
}

/**
 * The fleet list's row ⋮ menu (planet-proxy-KB#883). It renders the SAME
 * `ActionMenu` the server detail page does, against the same server-detail
 * record. That gives it the same items, per-role visibility, confirm dialogs and
 * admin gating, and the two menus cannot drift. It needs the detail, not the
 * list row: the list carries no protocols, and actions such as Re-provision
 * build their args from them.
 */
export function ServerRowActions({
  serverName,
  open,
  onClose,
  onIdle,
}: ServerRowActionsProps): JSX.Element {
  const { data: server, isError, error } = useFleetServer(serverName);

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!next) onClose(serverName);
    },
    [onClose, serverName],
  );

  useEffect(() => {
    if (!server && !open) onIdle(serverName);
  }, [server, open, onIdle, serverName]);

  if (server) {
    return (
      <LoadedServerRowActions
        server={server}
        open={open}
        onOpenChange={onOpenChange}
        onIdle={onIdle}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Actions — {serverName}</DialogTitle>
          <DialogDescription>
            {isError ? `Could not load ${serverName}: ${error.message}` : 'Loading server…'}
          </DialogDescription>
        </DialogHeader>
        <ServerPageLink name={serverName} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Owns the action hooks, above the dialog, so closing the menu keeps a started
 * run tracked and its task panel open, as on the detail page.
 */
function LoadedServerRowActions({
  server,
  open,
  onOpenChange,
  onIdle,
}: {
  server: ServerDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIdle: (name: string) => void;
}): JSX.Element {
  const action = useFleetAction(server);
  const lifecycle = useLifecycleAction(server);

  // Once a run is queued, close the menu so its task panel is the open modal.
  const actionTaskId = action.activeRun?.taskId;
  const lifecycleTaskId = lifecycle.activeRun?.taskId;

  useEffect(() => {
    if (actionTaskId !== undefined || lifecycleTaskId !== undefined) onOpenChange(false);
  }, [actionTaskId, lifecycleTaskId, onOpenChange]);

  const idle =
    !open &&
    action.activeRun === null &&
    lifecycle.activeRun === null &&
    !action.isSubmitting &&
    !lifecycle.isSubmitting;

  useEffect(() => {
    if (idle) onIdle(server.name);
  }, [idle, onIdle, server.name]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Actions — {server.name}</DialogTitle>
            <DialogDescription>
              The same actions, confirmations and role gates as the server page.
            </DialogDescription>
          </DialogHeader>

          <ActionMenu server={server} action={action} lifecycle={lifecycle} />

          <ServerPageLink name={server.name} />
        </DialogContent>
      </Dialog>

      <ServerActionPanels serverName={server.name} action={action} lifecycle={lifecycle} />
    </>
  );
}
